import moment from 'moment';
import {
  CLIENT_TIERS,
  CountBound,
  CountBounds,
  CountTier,
  MARKETING_INACTIVITY_PERIODS,
  MarketingInactivity,
  MERCHANT_LIST_URL_KEYS,
  MerchantListFilters,
  MerchantListUrlKey,
  NEVER_SENT,
  PASSAGE_TIERS,
  POINTS_TIERS,
  URL_DATE_FORMAT,
} from '../types/merchantListFilters';

type MerchantListApiParams = Record<string, string | number | boolean>;

const FILTER_BY_URL_KEY: Record<MerchantListUrlKey, keyof MerchantListFilters> = {
  createdFrom: 'createdFrom',
  createdTo: 'createdTo',
  minClients: 'minClients',
  maxClients: 'maxClients',
  minPassages: 'minPassages30d',
  maxPassages: 'maxPassages30d',
  minPoints: 'minPointsEarned30d',
  maxPoints: 'maxPointsEarned30d',
  lastSms: 'lastSms',
  lastPush: 'lastPush',
  category: 'categoryId',
};

const isEmpty = (value: string | number | undefined): value is undefined | '' => value === undefined || value === '';

const parseTier = (value: string | null, tiers: readonly CountTier[], bound: CountBound): number | undefined => {
  const tier = Number(value);
  return value && tiers.some(candidate => candidate.bound === bound && candidate.value === tier) ? tier : undefined;
};

// The select shows a single bound, so a hand-edited URL carrying both keeps the upper one.
const parseCountBounds = (
  params: URLSearchParams,
  minKey: MerchantListUrlKey,
  maxKey: MerchantListUrlKey,
  tiers: readonly CountTier[],
): CountBounds => {
  const max = parseTier(params.get(maxKey), tiers, 'max');
  return max === undefined ? { min: parseTier(params.get(minKey), tiers, 'min') } : { max };
};

const parseDate = (value: string | null): string | undefined =>
  value && moment(value, URL_DATE_FORMAT, true).isValid() ? value : undefined;

const isInactivityPeriod = (period: number): period is Exclude<MarketingInactivity, typeof NEVER_SENT> =>
  MARKETING_INACTIVITY_PERIODS.some(inactivityPeriod => inactivityPeriod === period);

export const isMarketingInactivity = (value: unknown): value is MarketingInactivity =>
  value === NEVER_SENT || (typeof value === 'number' && isInactivityPeriod(value));

const parseInactivity = (value: string | null): MarketingInactivity | undefined => {
  const inactivity = value === NEVER_SENT ? value : Number(value);
  return value !== null && isMarketingInactivity(inactivity) ? inactivity : undefined;
};

export const withoutEmptyValues = (filters: MerchantListFilters): MerchantListFilters => {
  const keptFilters: MerchantListFilters = {};
  MERCHANT_LIST_URL_KEYS.forEach(urlKey => {
    const filterKey = FILTER_BY_URL_KEY[urlKey];
    const value = filters[filterKey];
    if (!isEmpty(value)) {
      Object.assign(keptFilters, { [filterKey]: value });
    }
  });
  return keptFilters;
};

export const parseMerchantListFilters = (params: URLSearchParams): MerchantListFilters => {
  const createdFrom = parseDate(params.get('createdFrom'));
  const createdTo = parseDate(params.get('createdTo'));
  const isRangeReversed = createdFrom !== undefined && createdTo !== undefined && createdTo < createdFrom;
  const clients = parseCountBounds(params, 'minClients', 'maxClients', CLIENT_TIERS);
  const passages = parseCountBounds(params, 'minPassages', 'maxPassages', PASSAGE_TIERS);
  const points = parseCountBounds(params, 'minPoints', 'maxPoints', POINTS_TIERS);

  return withoutEmptyValues({
    createdFrom,
    createdTo: isRangeReversed ? undefined : createdTo,
    minClients: clients.min,
    maxClients: clients.max,
    minPassages30d: passages.min,
    maxPassages30d: passages.max,
    minPointsEarned30d: points.min,
    maxPointsEarned30d: points.max,
    lastSms: parseInactivity(params.get('lastSms')),
    lastPush: parseInactivity(params.get('lastPush')),
    categoryId: params.get('category') || undefined,
  });
};

export const writeMerchantListFilters = (url: URL, filters: MerchantListFilters): void => {
  MERCHANT_LIST_URL_KEYS.forEach(urlKey => {
    const value = filters[FILTER_BY_URL_KEY[urlKey]];
    if (isEmpty(value)) {
      url.searchParams.delete(urlKey);
    } else {
      url.searchParams.set(urlKey, String(value));
    }
  });
};

const toApiParam = (name: string, value: string | number | undefined): MerchantListApiParams =>
  isEmpty(value) ? {} : { [name]: value };

const toInactivityApiParams = (
  value: MarketingInactivity | undefined,
  inactiveDaysParam: string,
  neverSentParam: string,
): MerchantListApiParams => {
  if (value === undefined) {
    return {};
  }
  return value === NEVER_SENT ? { [neverSentParam]: true } : { [inactiveDaysParam]: value };
};

export const toMerchantListApiParams = (filters: MerchantListFilters): MerchantListApiParams => ({
  ...toApiParam('createdFrom', filters.createdFrom),
  ...toApiParam('createdTo', filters.createdTo),
  ...toApiParam('minClients', filters.minClients),
  ...toApiParam('maxClients', filters.maxClients),
  ...toApiParam('minPassages30d', filters.minPassages30d),
  ...toApiParam('maxPassages30d', filters.maxPassages30d),
  ...toApiParam('minPointsEarned30d', filters.minPointsEarned30d),
  ...toApiParam('maxPointsEarned30d', filters.maxPointsEarned30d),
  ...toApiParam('categoryId', filters.categoryId),
  ...toInactivityApiParams(filters.lastSms, 'smsInactiveDays', 'smsNeverSent'),
  ...toInactivityApiParams(filters.lastPush, 'pushInactiveDays', 'pushNeverSent'),
});

// An empty referential means the categories are not loaded yet, so the id cannot be judged.
export const dropUnknownCategory = (filters: MerchantListFilters, categoryIds: string[]): MerchantListFilters => {
  const { categoryId, ...otherFilters } = filters;
  if (!categoryId || categoryIds.length === 0 || categoryIds.includes(categoryId)) {
    return filters;
  }
  return otherFilters;
};
